import React from 'react';
import { motion } from 'framer-motion';

interface Product {
  name: string;
  period: string;
  riskLevel: string;
  allocation: number;
  percentage: number;
  expectedReturn: string;
}

interface Contact {
  branch: string;
  manager: string;
}

interface QuoteInfo {
  bankName: string;
  quoteNumber: string;
  generateDate: string;
}

interface CustomerInfo {
  name: string;
  annualIncome: number;
  riskTolerance: string;
  investmentAmount: number;
}

interface InvestmentGoal {
  title: string;
  description: string;
}

interface BankQuotationData {
  products: Product[];
  contact: Contact;
  quoteInfo: QuoteInfo;
  customerInfo: CustomerInfo;
  investmentGoal: InvestmentGoal;
}

interface ChatBankQuotationProps {
  data?: BankQuotationData;
  isLoading?: boolean;
}

const ChatBankQuotation: React.FC<ChatBankQuotationProps> = ({ data, isLoading = false }) => {
  // 格式化金额
  const formatAmount = (amount: number): string => {
    if (amount >= 10000) {
      return `${(amount / 10000).toFixed(0)}万`;
    }
    return amount.toLocaleString();
  };

  if (isLoading || !data) {
    return (
      <div style={{
        padding: '16px',
        backgroundColor: 'rgba(240, 248, 255, 0.5)',
        borderRadius: '8px',
        marginTop: '12px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '12px'
        }}>
          <div style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            backgroundColor: '#52c41a',
            marginRight: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <span style={{ fontWeight: 'bold', color: '#52c41a' }}>银行报价单</span>
        </div>
        
        {/* 加载骨架屏 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {[1, 2].map((i) => (
            <div key={i} style={{
              padding: '12px',
              backgroundColor: 'white',
              borderRadius: '6px',
              animation: 'pulse 1.5s ease-in-out infinite'
            }}>
              <div style={{
                height: '16px',
                backgroundColor: '#f0f0f0',
                borderRadius: '4px',
                marginBottom: '8px'
              }} />
              <div style={{
                height: '12px',
                backgroundColor: '#f0f0f0',
                borderRadius: '4px',
                width: '70%'
              }} />
            </div>
          ))}
        </div>
        
        <style>
          {`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
          `}
        </style>
      </div>
    );
  }

  const { products, contact, quoteInfo, customerInfo, investmentGoal } = data;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        padding: '16px',
        backgroundColor: 'rgba(240, 248, 255, 0.5)',
        borderRadius: '8px',
        marginTop: '12px'
      }}
    >
      {/* 标题 */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <div style={{
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          backgroundColor: '#52c41a',
          marginRight: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
        </div>
        <span style={{ fontWeight: 'bold', color: '#52c41a' }}>银行报价单</span>
      </div>

      {/* 报价信息 */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '6px',
          padding: '12px',
          marginBottom: '12px',
          border: '1px solid rgba(0, 0, 0, 0.06)'
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px'
        }}>
          <span style={{ fontWeight: 'bold', fontSize: '14px' }}>{quoteInfo.bankName}</span>
          <span style={{ fontSize: '12px', color: '#666' }}>{quoteInfo.generateDate}</span>
        </div>
        <div style={{ fontSize: '12px', color: '#999' }}>
          报价单号：{quoteInfo.quoteNumber}
        </div>
      </motion.div>

      {/* 投资目标 */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
        style={{
          backgroundColor: 'rgba(24, 144, 255, 0.05)',
          borderRadius: '6px',
          padding: '12px',
          marginBottom: '12px',
          border: '1px solid rgba(24, 144, 255, 0.1)'
        }}
      >
        <div style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '6px', color: '#1890ff' }}>
          {investmentGoal.title}
        </div>
        <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.4' }}>
          {investmentGoal.description}
        </div>
      </motion.div>

      {/* 客户信息 */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '6px',
          padding: '12px',
          marginBottom: '12px',
          border: '1px solid rgba(0, 0, 0, 0.06)'
        }}
      >
        <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px' }}>客户信息</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '12px' }}>
          <div><span style={{ color: '#666' }}>客户：</span>{customerInfo.name}</div>
          <div><span style={{ color: '#666' }}>年收入：</span>{formatAmount(customerInfo.annualIncome)}元</div>
          <div><span style={{ color: '#666' }}>投资金额：</span>{formatAmount(customerInfo.investmentAmount)}元</div>
          <div><span style={{ color: '#666' }}>风险偏好：</span>{customerInfo.riskTolerance}</div>
        </div>
      </motion.div>

      {/* 产品列表 */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px' }}>推荐产品</div>
        {products.map((product, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.4 + index * 0.1 }}
            style={{
              backgroundColor: 'white',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '8px',
              border: '1px solid rgba(0, 0, 0, 0.06)',
              position: 'relative'
            }}
          >
            {/* 产品名称和期限 */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '8px'
            }}>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '2px' }}>
                  {product.name}
                </div>
                <div style={{ fontSize: '11px', color: '#666' }}>
                  期限：{product.period} | 风险：{product.riskLevel}
                </div>
              </div>
              <div style={{
                backgroundColor: 'rgba(82, 196, 26, 0.1)',
                color: '#52c41a',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: 'bold'
              }}>
                {product.expectedReturn}
              </div>
            </div>

            {/* 配置信息 */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ fontSize: '12px' }}>
                <span style={{ color: '#666' }}>配置金额：</span>
                <span style={{ fontWeight: 'bold' }}>{formatAmount(product.allocation)}元</span>
              </div>
              <div style={{
                backgroundColor: '#f0f0f0',
                borderRadius: '10px',
                padding: '2px 8px',
                fontSize: '11px',
                color: '#666'
              }}>
                {product.percentage}%
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* 联系信息 */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, delay: 0.6 }}
        style={{
          backgroundColor: 'rgba(250, 173, 20, 0.05)',
          borderRadius: '6px',
          padding: '12px',
          border: '1px solid rgba(250, 173, 20, 0.2)'
        }}
      >
        <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '6px', color: '#faad14' }}>
          联系方式
        </div>
        <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.4' }}>
          <div style={{ marginBottom: '4px' }}>{contact.branch}</div>
          <div>{contact.manager}</div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ChatBankQuotation; 